import {gql, useQuery} from '@apollo/client'
import type {ComponentType} from 'react'
import React from 'react'
import hoistNonReactStatics from 'hoist-non-react-statics'
import {useAuth0} from '@auth0/auth0-react'
import {BO_profileQuery_, BO_profileQueryVariables} from './profile.generated'

/**
 * Profile
 */

type BoProfileFragment = BO_profileQuery_['profile'] & {refetchProfile: () => Promise<void>; isLoading: boolean}

const undefinedProfile: BoProfileFragment = {
  id: null,
  email: null,
  type: null,
  mts: [],
  mfus: [],
  isLoading: false,
  refetchProfile: null,
}

const ProfileContext = React.createContext<BoProfileFragment>(undefinedProfile)

const profileQuery = gql`
  query BO_profileQuery($data: ProfileDataInput!) {
    profile(data: $data) {
      id
      email
      type
      mfus {
        id
        mfu {
          id
        }
      }
      mts {
        id
        # Apollo caches objects without an "id" field with the object above.
        acl {
          canRead
          canWrite
          crafted
        }
        mt {
          id
          name
          type
          hasAmbulance
          hasTaxi
          hasVsl
          hasBariatric
          hasTpmr
          hasParamedical
          hasMedical
          # Used in delegate autocomplete
          associatedMts {
            id
            associateLabel
            associate {
              id
              name
              type
              hasAmbulance
              hasTaxi
              hasVsl
              hasBariatric
              hasTpmr
              hasParamedical
              hasMedical
              address {
                id
                formattedAddress
              }
            }
          }
          # Used by some autocomplete
          address {
            id
            formattedAddress
          }
        }
      }
    }
  }
`

const ProfileProvider: React.FC = ({children}) => {
  const {user, isAuthenticated, isLoading} = useAuth0()
  const userId = user?.['https://api.ambler.fr/']?.id
  const queryResult = useQuery<BO_profileQuery_, BO_profileQueryVariables>(profileQuery, {
    variables: {
      data: {
        userId,
      },
    },
    skip: isLoading || !userId || !isAuthenticated,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
  })

  const profile = React.useMemo(() => {
    return {
      ...undefinedProfile,
      ...queryResult.data?.profile,
      isLoading: queryResult.loading,
      refetchProfile: async () => {
        await queryResult.refetch()
      },
    }
  }, [queryResult.data, queryResult.refetch, queryResult.loading])

  return <ProfileContext.Provider value={profile}>{children}</ProfileContext.Provider>
}

export const withProfile = (Component: ComponentType) => {
  function WithProfile(props: any) {
    return (
      <ProfileProvider>
        <Component {...props} />
      </ProfileProvider>
    )
  }
  hoistNonReactStatics(WithProfile, Component)
  return WithProfile
}

export const useProfile = () => {
  const profile = React.useContext(ProfileContext)

  return profile
}
